import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import { isTesting } from '@embroider/macros';
import Component from '@glimmer/component';
import type { EmberChangeset } from 'ember-changeset';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import type Patient from 'parog-web/models/patient';
import type ErrorsService from 'parog-web/services/errors';
import FormyActions from './formy/actions';
import FormyCheckbox from './formy/checkbox';
import FormyDate from './formy/date';
import FormyField from './formy/field';
import FormyInput from './formy/input';
import FormySearch from './formy/search';
import FormySelect from './formy/select';

interface Signature {
  Element: HTMLFormElement;
  Args: {
    /** The EmberChangeset instance wrapping the model we want to edit */
    changeset: EmberChangeset;

    /** The function to be executed after the changeset has been sucessfully saved */
    onSuccess?: () => unknown;
    patient?: Patient;
  };
  Blocks: {
    default: [
      {
        changeset;
        Actions;
        Field;
        Input;
        Select;
        Date;
        Search;
        Checkbox;
      }
    ];
  };
}

// TODO: add other hash fields and rename Hash
export interface Hash {
  changeset: EmberChangeset;
}

export default class FormyComponent extends Component<Signature> {
  @service declare errors: ErrorsService;
  @service declare router: RouterService;

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);

    // Exiting the Formy is not only done by the form buttons
    // It could also happen when the user clicks on a link in the UI
    //
    // The Formy must then be informed when the router will transition
    // and in turn, alert the user if there are pending changes
    this.router.on('routeWillChange', this.showPendingChangesetAlert);
  }

  displayLocalErrors(): void {
    this.errors.displayError(this.errorMessage());
  }

  displayRemoteErrors(e): void {
    this.errors.defineErrors(e);
  }

  errorMessage(): string {
    return 'Veuillez corriger les erreurs avant de sauvegarder !';
  }

  validateAndSave = task(async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();

    await this.args.changeset.validate();

    if (this.args.changeset.isInvalid) {
      this.displayLocalErrors();
      return;
    }

    try {
      await this.args.changeset.save();
      await this.args.onSuccess?.();
    } catch (error) {
      if (!isTesting()) console.error(error);
      if (error === 'local-error') {
        this.displayLocalErrors();
      }
      if (error.errors) {
        this.displayRemoteErrors(error);
        // model.rollbackAttributes() removes the record from the store if it is new
        this.args.patient?.rollbackAttributes();

        return;
      }
      this.errors.displayError(
        "Nous sommes désolés, une erreur est survenue et nous n'avons pas pu sauvegarder vos modifications"
      );
    }
  });

  override willDestroy(): void {
    this.router.off('routeWillChange', this.showPendingChangesetAlert);
    super.willDestroy();
  }

  private showPendingChangesetAlert = async (transition: Transition) => {
    if (this.args.changeset.isPristine) return;
    if (this.stayingOnSameRoute(transition)) return;

    transition.abort();

    const response = await this.errors.showPendingChangesetError();
    if (response.isDismissed) return;

    // The user really want to loose its modifications
    this.args.changeset.rollback();
    transition.retry();
  };

  private stayingOnSameRoute(transition: Transition): boolean {
    return !!transition.to?.find(r => r.name === this.router.currentRouteName);
  }

  <template>
    {{! prettier-ignore }}
    <form
      {{on 'submit' (perform this.validateAndSave)}}
      ...attributes
      class='ui form {{if @changeset.isInvalid "error"}} {{if this.validateAndSave.isRunning " loading"}}'
    >
      {{yield
        (hash
          changeset=@changeset
          Actions=(component FormyActions formy=this)
          Field=(component FormyField formy=this)
          Input=(component FormyInput parent=this)
          Select=(component FormySelect parent=this)
          Date=(component FormyDate parent=this)
          Search=(component FormySearch parent=this)
          Checkbox=(component FormyCheckbox parent=this)
        )
      }}
    </form>
  </template>
}
